import PouchLoadService from "external-services/pouch-load-service";
import {
  ITableItem,
  ISectionLayoutItem,
  ISectionItem,
  SECTION_ANY_ID,
  SECTION_ANY_NAME,
  IBooking,
  IPouchRows, IObjectMap, ILayoutMinimalAbstract
} from 'shared-types/SharedTypes';
import LayoutsService from 'external-services/layouts-service/layouts.service';
import SectionLookupService from "external-services/section-lookup-service/sectionLookup.service";
import lodash, { cloneDeep } from 'lodash-es';
import {ISection} from "shared-types/DiaryTypes";
import { IGetTablesByLayoutsResponse } from 'external-services/tables-lookup-service/types';
import UtilsService from "external-services/utils-service/index";

const NS = 'TablesLookupService';

class TablesLookupService {

  private tablesLookup: Map<string, ITableItem>;

  getTablesLookup(): Map<string, ITableItem> {
    return this.tablesLookup;
  }

  public getTableNamesPrimaryFirst(
    tables: ITableItem[],
    primaryTableId?: string | null
  ): string[] {
    if (!tables?.length) return [];

    if (!primaryTableId) {
      return tables.map((t) => t.name).filter(Boolean) as string[];
    }

    const primary = tables.find((t) => t._id === primaryTableId);
    const rest = tables.filter((t) => t._id !== primaryTableId);

    return [...(primary ? [primary] : []), ...rest]
      .map((t) => t.name)
      .filter(Boolean) as string[];
  }

  async createTableLookup(): Promise<void> {
    this.tablesLookup = new Map<string, ITableItem>();

    const tables = await this.getAllTables();
    tables.forEach(t => {
      this.tablesLookup.set(t._id, t);
    });
  }

  private async getAllTables(): Promise<ITableItem[]> {
    const db = PouchLoadService.getDbLocal();
    if (!db) {
      return null;
    }

    const result: IPouchRows<ITableItem> = await Promise.resolve(db.allDocs({
      include_docs: true,
      attachments: false,
      startkey: 'table_',
      endkey: 'table_\uffff'
    })) as unknown as IPouchRows<ITableItem>;

    return result.rows.map(item => item.doc);
  }

  public getAllTablesByIds(ids: string[], cache: IObjectMap<ITableItem> = null, cacheKey: string = null): Promise<ITableItem[]> {
    return PouchLoadService.getDbLocal().allDocs({
      include_docs: true,
      keys: ids,
    }).then((tables: unknown) => {
      const tablesDocs: ITableItem[] = [];
      (tables as IPouchRows<ITableItem>).rows.forEach(table => {
        if (table.doc) {
          tablesDocs.push(table.doc);
        }
      });
      tablesDocs.sort((a,b) => this.tableCompare(a,b));
      if (cache && cacheKey) {
        cache[cacheKey] = tablesDocs;
      }
      return tablesDocs;
    })
  }

  public getTablesFromLayoutObjects(layouts: ILayoutMinimalAbstract[]): ITableItem[] {

    if (!layouts?.length) {
      return [];
    }

    let tableListUnsorted: ITableItem[] = [];

    const tableIsObject = (t: string | ITableItem): boolean => {
      return typeof t !== 'string';
    }

    /**
     * If tables are already objects, no need for a lookup. Assumes if 1st table is object, then all are objects.
     */
    if (layouts[0].tables?.length && tableIsObject(layouts[0].tables[0])) {
      tableListUnsorted = layouts.reduce((acc, layout) => {

        if (!layout.tables?.length) {
          return acc;
        }

        return [...acc, ...layout.tables];
      }, [])
    } else {

      /**
       * if tables are actually just string ids, then we need a lookup
       */
      const tableIds = lodash.uniq(
        layouts.reduce((acc, layout) => {

          if (!layout.tables?.length) {
            return acc;
          }

          return [...acc, ...layout.tables];
        }, [])
      );
      tableListUnsorted = tableIds.map(id => this.tablesLookup.get(id)).reverse();
    }

    const tableList = UtilsService.alphaSortFilter(tableListUnsorted, 'name');

    return tableList;
  }

  private getSectionIdsList(tables: ITableItem[]): string[] {
    const sectionIds: Set<string> = new Set();
    const tableLength = tables.length;

    for (let i = tableLength - 1; i >= 0; i--) {
      const table = tables[i];
      if (table.sectionId) {
        sectionIds.add(table.sectionId)
      }
    }

    return Array.from(sectionIds)
  }

  public async getTablesByLayouts(
    sectionLayouts: ISectionLayoutItem[], //TOdo: yunfang double check this interface, it should be ISectionLayoutItem[]
    bookingDate: Date,
    paramsPreferredSectionId: string
  ): Promise<IGetTablesByLayoutsResponse> {
    const layoutsIds = sectionLayouts.map((sectionLayout) => sectionLayout.layoutId);
    return this.getTablesByLayoutIds(layoutsIds, bookingDate, paramsPreferredSectionId);
  }

  public async getTablesByLayoutIds(
    layoutIds: string[],
    bookingDate: Date,
    paramsPreferredSectionId: string
  ): Promise<IGetTablesByLayoutsResponse> {
    const sectionsOrderLookUp: Map<string, number> = new Map();
    let preferredSectionId: string = paramsPreferredSectionId;
    let sections: ISectionItem[]

    try {
      const layouts = await LayoutsService.getByLayouts(
        layoutIds,
        bookingDate
      )
      const tablesList = layouts.tablesArray;

      const sectionIds = this.getSectionIdsList(tablesList);

      let sectionsByIds: ISection[];
      try {
        sectionsByIds = await SectionLookupService.getSectionsByIds(sectionIds)
      } catch (error) {
        console.warn(`SectionLookupService.getSectionsByIds error ${error}`)
        return {
          tablesList: [],
          preferredSectionId: '',
          sectionsOrderLookUp: new Map(),
          sections: []
        }
      }

      let preferredSectionIdMatch = false;

      sectionsByIds.forEach((section) => {
        if (section._id === paramsPreferredSectionId) {
          preferredSectionIdMatch = true;
        }
        sectionsOrderLookUp.set(section._id, section.order)
      })

      sections = cloneDeep(sectionsByIds) as ISectionItem[];
      sections.unshift({
        _id: SECTION_ANY_ID,
        name: SECTION_ANY_NAME,
        order: -1
      });

      if (!preferredSectionIdMatch) {
        preferredSectionId = SECTION_ANY_ID;
      }
      tablesList.sort((a,b) => this.tableCompare(a,b));
      return {
        tablesList,
        preferredSectionId,
        sectionsOrderLookUp,
        sections,
      }
    } catch (error) {
      console.warn(`get table by layout error ${error}`)
      return {
        tablesList: [],
        preferredSectionId: '',
        sectionsOrderLookUp: new Map(),
        sections: []
      }
    }
  }

  public getPrimaryTableId(tableList: ITableItem[], primaryTableId: string, displayTableId: string): { primaryTable: ITableItem, showAsLinked: boolean } {

    if (tableList.length <= 1 || !displayTableId) {
      return {
        showAsLinked: false,
        primaryTable: undefined
      }
    }

    const primaryTableIdCloned = primaryTableId || tableList[0]?._id

    if (primaryTableIdCloned !== displayTableId) {
      const primaryTable: ITableItem = tableList.find(({ _id }) => _id === primaryTableIdCloned)
      return {
        showAsLinked: true,
        primaryTable
      }
    }

    return {
      showAsLinked: false,
      primaryTable: undefined
    }
  }

  public getTableListAsString(tableList: ITableItem[], tablesLookup: Map<string, ITableItem>, showT = true): string {
    return tableList.reduce((result, { _id }) => {
      if (tablesLookup.has(_id)) {
        const tableName = tablesLookup.get(_id).name; // https://nowbookit.atlassian.net/browse/NBI-4728 Hotfix where the updated table name was not getting picked. - we need to choose the latest name from the tableLookup.
        // result.push(`T${tableName}`);
        showT ? result.push(`T${tableName}`) : result.push(`${tableName}`); // for FP we do not show T before table names
      }
      return result
    }, []).join(',') // concat string array
  }

  public getTotalTableCapacity(tableList: ITableItem[], tablesLookup: Map<string, ITableItem>): number {
    return tableList.reduce((result, { capacity, _id }) => {
      return result + (tablesLookup.has(_id) ? capacity : 0)
    }, 0)
  }

  public isTableAvailable(booking: IBooking): boolean {
    const hasNoTables = !booking.tables || booking.tables.length === 0;
    return hasNoTables && booking.tablesAvaiable;
  }

  private tableCompare(a: ITableItem, b: ITableItem ): number {
    //protect against some edge cases
    if(!a || !b){
      return 0;
    }

    //in case table is corrutp and has null name.
    const aName = (a.name ?? "").toString();
    const bName = (b.name ?? "").toString();

    //Sometimes a.name is a number, despite the interface, investigate
    return aName.localeCompare(bName, undefined, {numeric: true, sensitivity: 'base'});
  }
  public getTablesInfoFromTableList = (tableList: ITableItem[]): { name: string, _id: string, layoutName: string}[] => {
      return tableList.map((table) => {
          return {
              name: table.name,
              _id: table._id,
              layoutName: table.layoutName,
          };
      });
  };

  public getTableByIdFromCache(id: string): ITableItem {
    return this.tablesLookup?.get(id);
  }
}

const instance = new TablesLookupService();
export default instance as TablesLookupService;

